import { useLocation } from '@reach/router'
import { graphql, Link, navigate } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import get from 'lodash.get'
import React, { useEffect, useState } from 'react'
import colors from '../theme/colors'
import styled from 'styled-components'
import Layout from '../components/layout'
import Responsive from '../components/responsive'
import Seo from '../components/seo'
import Title from '../components/titles'

const BusinessCasePage = ({ data }) => {
    const location = useLocation()
    const businessCases = get(data, 'allPrismicBusinessCase.nodes')
    const uniqueTags = [...new Set(businessCases.map(item => item.data.tag))]
    const [filter, setFilter] = useState(null)
    const formatTag = (tag) =>
        tag.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')

    useEffect(() => {
        const params = new URLSearchParams(location.search)
        const tagParam = params.get('tag')
        if ( tagParam ) {
            setFilter(tagParam)
        }
    }, [location.search])

    const handleTagClick = (tag) => {
        const formattedTag = formatTag(tag)
        const newFilter = filter === formattedTag ? null : formattedTag
        setFilter(newFilter)

        const params = new URLSearchParams(location.search)
        if ( newFilter ) {
            params.set('tag', newFilter)
        } else {
            params.delete('tag')
        }
        navigate(`?${params.toString()}`)
    }

    const filteredCases = filter
        ? businessCases.filter((caseItem) => formatTag(caseItem.data.tag) === filter)
        : businessCases

    return (
        <>
            <Seo title={'business cases'} />
            <Layout withMiniHeader>
                <StyledSection>
                    <Responsive>
                        <Title as='h1' size='large' withDot>
                            Business Cases
                        </Title>
                        <StyledTagRow>
                            {uniqueTags.map((tag) => {
                                return (
                                    <StyledTag key={tag}
                                               $isActive={formatTag(tag) === filter}
                                               onClick={() => handleTagClick(tag)}
                                               aria-label={`Filtrer les tags: ${tag}`}>
                                        {tag}
                                    </StyledTag>
                                )
                            })}
                        </StyledTagRow>
                        <StyledGridContainer>
                            {filteredCases.map((item, index) => {
                                const { title, cover } = item.data
                                const { alt, thumbnails } = cover
                                const image = get(thumbnails, 'cover_landscape.localFile.childImageSharp.gatsbyImageData')

                                return (
                                    <Link to={`/business-case/${item.uid}`} key={`${title.text}-${index}`}>
                                        <StyledSlide className={'slideCustom'}>
                                            <StyledImage alt={alt || title.text} image={image} />
                                            <StyledLabelContainer>
                                                <StyledLabel>{title.text}</StyledLabel>
                                            </StyledLabelContainer>
                                        </StyledSlide>
                                    </Link>
                                )
                            })}
                        </StyledGridContainer>
                    </Responsive>
                </StyledSection>
            </Layout>
        </>
    )
}

const StyledSection = styled.section`
  padding: 4rem 0;
`
const StyledTagRow = styled.div`
  display: flex;
  gap: 8px;
  margin: 1rem 0;
`
const StyledTag = styled.span`
  background-color: ${({ $isActive }) => ($isActive ? colors.darkBlue : colors.beige80)};
  color: ${({ $isActive }) => ($isActive ? colors.white : colors.darkBlue)};;
  border-radius: 6px;
  padding: 6px 10px;
  cursor: pointer;
`
const StyledGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  row-gap: 28px;
  column-gap: 6px;
  padding: 16px 0;

  @media (max-width: 670px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 450px) {
    grid-template-columns: 1fr;
  }
`
const StyledSlide = styled.div`
  height: 400px;
  width: auto;
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  margin: 0 8px;
  transition: scale 0.5s;

  &:hover {
    scale: 1.1;
    z-index: 2;
  }
`

const StyledImage = styled(GatsbyImage)`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const StyledLabelContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 10px;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(0, 0, 0, 0.55) 100%);
  color: white;
  text-align: center;
`

const StyledLabel = styled.p`
  margin: 0;
  font-size: 18px;
  font-weight: bold;
  padding: 10px;
`

export const query = graphql`
    query BusinessPage {
        allPrismicBusinessCase {
            nodes {
                uid
                data {
                    tag
                    title {
                        text
                    }
                    cover {
                        alt
                        thumbnails {
                            cover_landscape {
                                localFile {
                                    childImageSharp {
                                        gatsbyImageData
                                    }
                                }
                            }
                            cover_portrait {
                                localFile {
                                    childImageSharp {
                                        gatsbyImageData
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`
export default BusinessCasePage